import {
    AXIS_GREY,
    CHART_BLUE,
    MONTH_ABBREVIATIONS,
    OUTLINE_RADIUS,
    TOOLTIP_BORDER_RADIUS,
    TOOLTIP_DARK,
    TOOLTIP_DISTANCE,
    TOOLTIP_HEIGHT,
    TOOLTIP_SUB_TEXT_HEIGHT,
    TOOLTIP_TEXT_HEIGHT,
    TOOLTIP_WIDTH,
} from './chart-constants'
import { parseDateString, type TooltipData } from './chart-utils'

interface ChartTooltipProps {
    chartType: 'bar' | 'curve' | 'line'
    containerWidth: number
    data: TooltipData
    dataType: 'number' | 'price'
}

function formatTooltipValue(value: number, type: 'number' | 'price'): string {
    if (type === 'price') {
        return `$${Math.round(value)}`
    }
    return String(value)
}

function formatTooltipLabel(xVal: string | number, chartType: string): string {
    const showMonth = chartType === 'bar' && typeof xVal === 'number'
    const showDate = chartType === 'bar' && typeof xVal === 'string'
    const isLineGraph = chartType === 'line' || chartType === 'curve'

    if (showMonth) {
        return MONTH_ABBREVIATIONS[xVal] ?? String(xVal)
    }
    if (showDate) {
        return parseDateString(xVal).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
    if (isLineGraph) {
        return `${xVal} days`
    }
    return String(xVal)
}

export function ChartTooltip({ chartType, containerWidth, data, dataType }: ChartTooltipProps) {
    const { xPos, xVal, yPos, yVal } = data
    const isLineGraph = chartType === 'line' || chartType === 'curve'

    const tooltipBaseOffset = TOOLTIP_HEIGHT + TOOLTIP_DISTANCE
    const tooltipOffset = isLineGraph ? tooltipBaseOffset + OUTLINE_RADIUS : tooltipBaseOffset

    let tooltipXPosition = isLineGraph ? xPos - TOOLTIP_WIDTH / 2 : xPos - TOOLTIP_WIDTH / 4
    let tooltipYPosition = isLineGraph ? yPos + TOOLTIP_HEIGHT / 2 : 0

    if (xPos + TOOLTIP_WIDTH > containerWidth) {
        tooltipXPosition = containerWidth - TOOLTIP_WIDTH
    }

    if (yPos - tooltipOffset > 0) {
        tooltipYPosition = yPos - tooltipOffset
    }

    return (
        <g>
            <rect
                height={TOOLTIP_HEIGHT}
                rx={TOOLTIP_BORDER_RADIUS}
                width={TOOLTIP_WIDTH}
                x={tooltipXPosition}
                y={tooltipYPosition}
                fill="white"
                stroke={AXIS_GREY}
                strokeWidth={1}
                filter="drop-shadow(0 4px 8px rgba(23,25,27,0.08))"
            />
            <text
                x={tooltipXPosition + TOOLTIP_WIDTH / 2}
                y={tooltipYPosition + TOOLTIP_TEXT_HEIGHT}
                fill={CHART_BLUE}
                fontWeight="bold"
                fontSize={20}
                textAnchor="middle"
            >
                {formatTooltipValue(yVal, dataType)}
            </text>
            <text
                x={tooltipXPosition + TOOLTIP_WIDTH / 2}
                y={tooltipYPosition + TOOLTIP_SUB_TEXT_HEIGHT}
                fill={TOOLTIP_DARK}
                fontSize={14}
                textAnchor="middle"
            >
                {formatTooltipLabel(xVal, chartType)}
            </text>
        </g>
    )
}
